import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { FlatNode } from './model/flat.node.interface';
import { SimpleNode } from './model/simple.node.interface';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-select-hierarquical-list',
  templateUrl: './select-hierarquical-list.component.html',
  styleUrls: ['./select-hierarquical-list.component.scss'],
})
export class SelectHierarquicalListComponent implements OnInit, OnDestroy {
  @Output() selectEvent = new EventEmitter<FlatNode[]>();
  @Output() deSelectEvent = new EventEmitter<FlatNode[]>();
  @Input() treeData: SimpleNode[] = [];
  @Input() initialSelection: string[] = [];
  @Input() hideButtons = false;

  constructor() {}

  private _transformer = (node: SimpleNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      visible: true,
      id: node.id || '0',
    };
  };

  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: FlatNode) => node.expandable;

  allSelected = false;

  checklistSelection = new SelectionModel<FlatNode>(true);

  selectAllClicked() {
    this.allSelected = !this.allSelected;
    this.selectRoot(this.allSelected);

    //emit case no buttons
    if (this.hideButtons) {
      this.emitEvent();
    }
  }

  private toggleParent(node: FlatNode): void {
    const parentNode = this.getParentNode(node) as FlatNode;
    if (parentNode) {
      const descendants = this.treeControl.getDescendants(parentNode);
      descendants.every((child) => this.checklistSelection.isSelected(child))
        ? this.checklistSelection.select(parentNode)
        : this.checklistSelection.deselect(parentNode);
      this.toggleParent(parentNode);
    }
  }

  private selectRoot(val: boolean) {
    const values = this.dataSource._expandedData.value as Array<FlatNode>;

    values.forEach((n: FlatNode) => {
      const descendants = this.treeControl.getDescendants(n);
      val
        ? this.checklistSelection.select(...descendants, n)
        : this.checklistSelection.deselect(...descendants, n);
    });
  }

  handleSearch(searchTxt: string) {
    const values = this.dataSource._flattenedData.value as Array<FlatNode>;
    if (searchTxt) {
      values.forEach((val) => {
        val.visible = val.name.includes(searchTxt);
      });
      this.treeControl.expandAll();
    } else {
      values.forEach((val) => {
        val.visible = true;
      });
    }
  }

  descendantsAllSelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    return descendants.every((child) =>
      this.checklistSelection.isSelected(child)
    );
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: FlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);

    this.checkAllSelected();
  }

  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.dataSource.data = this.treeData;
    this.selectInitial();
    this.subscriptions.add(this.handleDeselection());
  }

  deselectedNodes: FlatNode[] = [];

  private handleDeselection() {
    return this.checklistSelection.changed.subscribe((change) => {
      if (change.removed && change.removed.length > 0) {
        this.deselectedNodes.push(...change.removed);
      }
      if (change.added && change.added.length > 0) {
        this.deselectedNodes = this.deselectedNodes.filter(
          (n) => !change.added.includes(n)
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private selectInitial() {
    const values = this.dataSource._flattenedData.value as Array<FlatNode>;
    values.forEach((n: FlatNode) => {
      if (this.initialSelection.includes(n.id)) {
        this.checklistSelection.select(n);
        this.toggleParent(n);
      }
    });
    this.allSelected =
      this.checklistSelection.selected.length === values.length;
  }

  emitEvent(): void {
    this.selectEvent.emit(
      this.checklistSelection.selected.filter((node) => !node.expandable)
    );
    this.deSelectEvent.emit(this.deselectedNodes.filter((n) => !n.expandable));
  }

  checkAllSelected(): void {
    this.allSelected =
      this.checklistSelection.selected.length ===
      this.dataSource._flattenedData.value.length;

    //emit case no buttons
    if (this.hideButtons) {
      this.emitEvent();
    }
  }

  getLevel = (node: FlatNode) => node.level;

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: FlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
    this.checkAllSelected();
  }
  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: FlatNode): void {
    let parent: FlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }
  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: FlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }
  /* Get the parent node of a node */
  getParentNode(node: FlatNode): FlatNode | null {
    const currentLevel = this.getLevel(node);
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }
  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: FlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) =>
      this.checklistSelection.isSelected(child)
    );
    return result && !this.descendantsAllSelected(node);
  }

  listPartiallySelected(): boolean {
    return (
      this.checklistSelection.selected.length > 0 &&
      this.checklistSelection.selected.length !==
        this.dataSource._flattenedData.value.length
    );
  }

  resetSelection() {
    this.allSelected = false;
    this.selectRoot(false);
  }

  applySelection() {
    this.emitEvent();
  }
}
